import React from "react";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";

/**
 * Reusable simplified rules-of-golf content. Used:
 *   1. On the /help page as a section.
 *   2. Inside a Sheet on the live scorecard, so a player can look up a rule
 *      mid-round without leaving the round.
 *
 * Keep this component presentational (no routing / no auth) so it can be
 * dropped into any container.
 */
export default function RulesCheatSheet({ idPrefix = "rule", className = "card-default px-4" }) {
  return (
    <Accordion type="single" collapsible className={className} data-testid="rules-accordion">
      <AccordionItem value="basics">
        <AccordionTrigger data-testid={`${idPrefix}-basics`}>The basics — what counts as a stroke</AccordionTrigger>
        <AccordionContent className="text-sm text-[#0F2537]/80 leading-relaxed space-y-2">
          <p>A <b>stroke</b> is any forward motion of the club made with intent to hit the ball. Whiffs count. Practice swings don't.</p>
          <p>Play the ball <b>as it lies</b> unless a rule lets you move it. Don't improve your lie, stance or swing path by pressing down grass, breaking branches, etc.</p>
          <p>Count every stroke + every penalty stroke. Most amateur "rule arguments" boil down to forgetting a penalty stroke.</p>
        </AccordionContent>
      </AccordionItem>
      <AccordionItem value="tee">
        <AccordionTrigger data-testid={`${idPrefix}-tee`}>Teeing off</AccordionTrigger>
        <AccordionContent className="text-sm text-[#0F2537]/80 leading-relaxed space-y-2">
          <p>Tee your ball <b>between</b> the markers and no more than <b>two club-lengths behind</b> them.</p>
          <p>If you swing and miss the ball while it's on the tee, that's <b>1 stroke</b> — the ball is now in play and you keep going from wherever it ends up.</p>
          <p>If the ball falls off the tee before you swing, no penalty — just re-tee.</p>
        </AccordionContent>
      </AccordionItem>
      <AccordionItem value="oob">
        <AccordionTrigger data-testid={`${idPrefix}-oob`}>Out of bounds &amp; lost ball</AccordionTrigger>
        <AccordionContent className="text-sm text-[#0F2537]/80 leading-relaxed space-y-2">
          <p><b>OB</b> is anything beyond the white stakes / fence line.</p>
          <p><b>Penalty:</b> stroke + distance. You add 1 penalty stroke <em>and</em> replay the shot from the original spot. (Hit OB from the tee → re-tee, hitting your 3rd shot.)</p>
          <p>You have <b>3 minutes</b> to search for a ball. After that it's lost — same stroke-and-distance penalty.</p>
          <p><b>Provisional ball:</b> if you think your shot might be OB or lost, announce "provisional" and play another from the same spot. Saves the long walk back.</p>
        </AccordionContent>
      </AccordionItem>
      <AccordionItem value="penalty-area">
        <AccordionTrigger data-testid={`${idPrefix}-penalty-area`}>Penalty areas (red &amp; yellow)</AccordionTrigger>
        <AccordionContent className="text-sm text-[#0F2537]/80 leading-relaxed space-y-2">
          <p>Stakes mark <b>penalty areas</b> — usually water, jungle or the lava-rock at Natadola's 13th.</p>
          <p>Add <b>1 penalty stroke</b> and choose one option:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Play it as it lies (no relief, no penalty) — if you can swing.</li>
            <li><b>Stroke and distance</b> — replay from where you last hit.</li>
            <li><b>Back on the line</b> — drop anywhere on the line from the pin through where the ball entered the area, going back as far as you like.</li>
            <li><b>Red stake only — lateral relief:</b> drop within <b>2 club-lengths</b> of where the ball last crossed, no closer to the hole.</li>
          </ul>
        </AccordionContent>
      </AccordionItem>
      <AccordionItem value="unplayable">
        <AccordionTrigger data-testid={`${idPrefix}-unplayable`}>Unplayable lie</AccordionTrigger>
        <AccordionContent className="text-sm text-[#0F2537]/80 leading-relaxed space-y-2">
          <p>You can declare your ball <b>unplayable</b> anywhere on the course except inside a penalty area. <b>1 penalty stroke</b> then pick one:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li><b>Stroke and distance</b> — replay from previous spot.</li>
            <li><b>Back on the line</b> from the pin through your ball, going back as far as you want.</li>
            <li><b>2 club-lengths</b>, no closer to the hole.</li>
          </ul>
          <p>In a bunker, the third option (2 club-lengths) must stay in the bunker, or take <b>back-on-line outside the bunker for 2 strokes</b>.</p>
        </AccordionContent>
      </AccordionItem>
      <AccordionItem value="bunker">
        <AccordionTrigger data-testid={`${idPrefix}-bunker`}>Bunkers</AccordionTrigger>
        <AccordionContent className="text-sm text-[#0F2537]/80 leading-relaxed space-y-2">
          <p>You can <b>touch</b> the sand with your hand, lean on a club, or remove loose impediments (leaves, stones) — but you <b>cannot</b>:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li>Test the sand by deliberately touching it for info.</li>
            <li>Ground your club <em>right next to</em> the ball.</li>
            <li>Touch the sand on your back-swing.</li>
          </ul>
          <p>Breaking any of those = <b>2 penalty strokes</b>. Rake the bunker after you're done — courtesy, not rule.</p>
        </AccordionContent>
      </AccordionItem>
      <AccordionItem value="putting-green">
        <AccordionTrigger data-testid={`${idPrefix}-green`}>On the putting green</AccordionTrigger>
        <AccordionContent className="text-sm text-[#0F2537]/80 leading-relaxed space-y-2">
          <p>You can <b>mark</b>, <b>lift</b> and <b>clean</b> your ball. You can repair spike marks and pitch marks. You can leave the flagstick in or pull it — your choice, no penalty either way (since 2019).</p>
          <p>If your ball hits another ball on the green during a putt, in stroke play that's <b>2 penalty strokes</b> — so always ask others to mark before you putt.</p>
          <p>Hitting the flagstick while putting from <em>off</em> the green: no penalty.</p>
        </AccordionContent>
      </AccordionItem>
      <AccordionItem value="cart-path">
        <AccordionTrigger data-testid={`${idPrefix}-cart-path`}>Free drops — cart paths, sprinklers, casual water</AccordionTrigger>
        <AccordionContent className="text-sm text-[#0F2537]/80 leading-relaxed space-y-2">
          <p>Cart paths, sprinkler heads, GUR (ground under repair), and casual water (puddles) all give you <b>free relief — no penalty</b>.</p>
          <p>Find the <b>nearest point of complete relief</b> (no closer to the hole), then drop within <b>1 club-length</b> of that point.</p>
          <p>Drop from <b>knee height</b>. If the ball rolls back into trouble, re-drop once. If it still rolls back, place it where it last hit the ground.</p>
        </AccordionContent>
      </AccordionItem>
      <AccordionItem value="wrong-ball">
        <AccordionTrigger data-testid={`${idPrefix}-wrong-ball`}>Wrong ball / wrong place</AccordionTrigger>
        <AccordionContent className="text-sm text-[#0F2537]/80 leading-relaxed space-y-2">
          <p>Hit someone else's ball? <b>2 penalty strokes</b> in stroke play, then go find and play your own. Mark your ball with a Sharpie at the start of every round.</p>
          <p>Played from the wrong tee or wrong spot? <b>2 strokes</b> — but if it's a serious advantage you must correct it before teeing off the next hole.</p>
        </AccordionContent>
      </AccordionItem>
      <AccordionItem value="etiquette">
        <AccordionTrigger data-testid={`${idPrefix}-etiquette`}>Pace, etiquette &amp; safety</AccordionTrigger>
        <AccordionContent className="text-sm text-[#0F2537]/80 leading-relaxed space-y-2">
          <p>Not enforced as strokes, but important:</p>
          <ul className="list-disc ml-5 space-y-1">
            <li><b>Ready golf</b> — whoever's ready hits, regardless of who's "away".</li>
            <li><b>Pace:</b> 4 hours 15 min for 18 holes is the Fiji-Golf-Inc standard. Keep up with the group ahead, not just ahead of the group behind.</li>
            <li><b>Yell "FORE!"</b> immediately if your shot heads toward anyone.</li>
            <li><b>Repair</b> your pitch marks on the green and replace divots in the fairway.</li>
            <li><b>Wave through</b> faster groups behind you if you're searching.</li>
          </ul>
        </AccordionContent>
      </AccordionItem>
      <AccordionItem value="match-vs-stroke">
        <AccordionTrigger data-testid={`${idPrefix}-match-vs-stroke`}>Match play vs stroke play — key differences</AccordionTrigger>
        <AccordionContent className="text-sm text-[#0F2537]/80 leading-relaxed space-y-2">
          <p><b>Stroke play:</b> count every stroke, lowest total wins. Standard for handicap-counting rounds and most tournaments in this app.</p>
          <p><b>Match play:</b> hole-by-hole. Win the hole = 1 up. Most penalties = "loss of hole" instead of stroke-count penalties. You can concede putts to speed things up.</p>
          <p>Bula Golf supports both — pick the format on the New Round screen.</p>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
